import { Link } from "react-router-dom";
import { money } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Plus, Lightning } from "@phosphor-icons/react";

export default function ProductCard({ product }) {
  const { add } = useCart();
  const navigate = useNavigate();
  const variant = product.variants?.[0] || null;
  const price = variant ? variant.price_cents : product.price_cents;
  const priceLabel = product.variants?.length
    ? money(Math.min(...product.variants.map((v) => v.price_cents)))
    : money(product.price_cents);

  const stopThen = (fn) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    fn();
  };
  const addToCart = () => {
    add(product, variant, 1);
    toast.success(`${product.name} added to cart`, { duration: 1600 });
  };
  const buyNow = () => {
    add(product, variant, 1);
    navigate("/cart");
  };

  return (
    <div
      className="card-lift group block rounded-2xl overflow-hidden"
      data-testid={`product-card-${product.slug}`}
    >
      <Link to={`/product/${product.slug}`} className="block" tabIndex={-1}>
        <div className="aspect-[4/5] bg-black/30 border-b border-white/10 relative overflow-hidden">
          {product.image_url ? (
            <img
              src={product.image_url}
              alt={product.name}
              className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.06] opacity-90 group-hover:opacity-100"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full grid-bg" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent pointer-events-none" />
          <div className="absolute top-3 left-3 chip">
            {product.category_slug?.replace(/-/g, " ")}
          </div>
          {product.purity && (
            <div className="absolute bottom-3 left-3 chip chip-accent max-w-[70%] truncate">
              {product.purity}
            </div>
          )}
        </div>
      </Link>

      <div className="p-5 bg-[color:var(--prmu-surface)]">
        <Link to={`/product/${product.slug}`} className="block">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="font-display-plain text-lg font-semibold leading-tight truncate">
                {product.name}
              </div>
              {product.subtitle && (
                <div className="text-sm text-zinc-500 mt-0.5">{product.subtitle}</div>
              )}
            </div>
            <ArrowUpRight
              size={18}
              weight="bold"
              className="shrink-0 text-zinc-500 transition-all duration-200 group-hover:text-blue-500 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
            />
          </div>
          <div className="mt-4 flex items-center justify-between">
            <div className="font-mono text-sm text-white">{priceLabel}</div>
            {variant?.label && <div className="overline">{variant.label}</div>}
          </div>
        </Link>

        <div className="mt-4 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={stopThen(addToCart)}
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full border border-white/15 hover:border-blue-500 hover:text-blue-400 transition-colors text-xs font-semibold"
            data-testid={`card-add-${product.slug}`}
          >
            <Plus size={13} weight="bold" /> Add
          </button>
          <button
            type="button"
            onClick={stopThen(buyNow)}
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-white text-black hover:bg-blue-500 hover:text-white transition-colors text-xs font-semibold"
            data-testid={`card-buy-${product.slug}`}
          >
            <Lightning size={13} weight="fill" /> Buy now
          </button>
        </div>
      </div>
    </div>
  );
}
